import { z } from 'zod';

/**
 * Step 2 (agregado) — Frota / LMI Único (RD Equipamentos, ramo 0171, fluxos
 * `frota`/`lmi_unico`).
 *
 * Espelha `RdFleetPayload` (`@/services/quotes/rd-equipamentos/types`) campo a
 * campo — valores já NORMALIZADOS (`riskValue`/`totalLmi`/`fleetCoverages` em
 * centavos). O componente (`step-frota.component.tsx`) converte os valores
 * brutos do formulário (strings mascaradas em moeda) para este formato antes
 * de chamar `safeParse` (ver `use-set-rd-fleet.hook.ts`).
 */
export const StepFrotaSchema = z.object({
  riskValue: z.number().int().positive('Informe o valor em risco'),
  totalLmi: z.number().int().positive('Informe o LMI total'),
  plano: z.string().min(1, 'Selecione um plano'),
  // Endereço do risco agregado. Obrigatório no LMI Único (estacionário), mas
  // a checagem depende de `productType`, que vem em runtime — fica no
  // componente, não aqui, como em `step-parametros.schema.ts`.
  riskCep: z.string().optional(),
  riskAddress: z.string().optional(),
  riskNumber: z.string().optional(),
  riskComplement: z.string().optional(),
  riskDistrict: z.string().optional(),
  riskCity: z.string().optional(),
  riskState: z.string().max(2).optional(),

  fleetCoverages: z.record(z.string(), z.number().int().nonnegative()),
  /**
   * Período indenitário do agregado, em meses. Um só para a proposta inteira:
   * não há itens, e a cobertura que o pede aparece uma vez no quadro de LMIs.
   * Opcional aqui porque quem o exige é o motor, no CÁLCULO — a gravação
   * aceita vazio para não travar proposta antiga.
   */
  fleetIndemnityMonths: z.number().int().positive().optional(),
});

/** Formato normalizado (payload-ready) — ver nota acima. */
export type StepFrotaValues = z.infer<typeof StepFrotaSchema>;

/**
 * Formato bruto do formulário — `riskValue`/`totalLmi` e cada entrada de
 * `fleetCoverages` ficam como string mascarada em moeda (input controlado) e
 * só são convertidos para `StepFrotaValues` no submit.
 */
export type StepFrotaFormValues = {
  riskValue: string;
  totalLmi: string;
  plano: string;
  riskCep: string;
  riskAddress: string;
  riskNumber: string;
  riskComplement: string;
  riskDistrict: string;
  riskCity: string;
  riskState: string;
  fleetCoverages: Record<string, string>;
  /** Meses; `0` = ainda não escolhido (o rádio não marca nenhum). */
  indemnityMonths: number;
};
